import React, { useEffect, useState } from 'react';
import { Tag, Trash2, Loader2, ArrowLeft } from 'lucide-react';
import { fetchItems, updateItem } from '../api/itemsApi';
import { useNavigate } from 'react-router-dom';

const AdminDiscounts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadDiscountedProducts();
    }, []);

    const loadDiscountedProducts = async () => {
        try {
            setLoading(true);
            const allProducts = await fetchItems();

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Filter products that have a discount > 0 AND a valid end date (today or future)
            const discounted = allProducts.filter(p => {
                if (!p.discount || p.discount <= 0 || !p.discountEndDate) return false;

                const endDate = new Date(p.discountEndDate + 'T00:00:00');
                endDate.setHours(0, 0, 0, 0);

                return endDate >= today;
            });

            setProducts(discounted);
        } catch (error) {
            console.error('Error loading discounted products:', error);
        } finally {
            setLoading(false);
        }
    };

    const removeDiscount = async (product) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar el descuento del producto "${product.nombre}"?`)) {
            return;
        }

        try {
            setUpdatingId(product.id);
            // We set discount to 0 and remove the end date
            await updateItem(product.id, {
                ...product,
                discount: 0,
                discountEndDate: null
            });

            // Remove from local state
            setProducts(products.filter(p => p.id !== product.id));
        } catch (error) {
            console.error('Error removing discount:', error);
            alert('Error al eliminar el descuento');
        } finally {
            setUpdatingId(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 mb-4">
                <button
                    onClick={() => navigate('/admin')}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <ArrowLeft className="w-6 h-6 text-gray-600" />
                </button>
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Productos con Descuento</h1>
                    <p className="text-gray-500 mt-1">Gestiona los descuentos individuales de los productos</p>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-600 font-medium text-sm uppercase tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Producto</th>
                                <th className="px-6 py-4 text-center">Descuento</th>
                                <th className="px-6 py-4 text-center">Fin Descuento</th>
                                <th className="px-6 py-4 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {products.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                                        No hay productos con descuento activo actualmente.
                                    </td>
                                </tr>
                            ) : (
                                products.map((product) => (
                                    <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <img
                                                    src={product.imagen}
                                                    alt={product.nombre}
                                                    className="w-12 h-12 rounded-lg object-cover bg-gray-100"
                                                    onError={(e) => e.target.src = 'https://via.placeholder.com/150'}
                                                />
                                                <div>
                                                    <p className="font-medium text-gray-900">{product.nombre}</p>
                                                    <p className="text-sm text-gray-500">{product.linea}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                                                <Tag className="w-4 h-4" />
                                                {product.discount}%
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center text-gray-600">
                                            {product.discountEndDate ? new Date(product.discountEndDate + 'T00:00:00').toLocaleDateString() : 'No definida'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => removeDiscount(product)}
                                                disabled={updatingId === product.id}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors inline-flex items-center gap-2 font-medium"
                                                title="Eliminar Descuento"
                                            >
                                                {updatingId === product.id ? (
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                ) : (
                                                    <>
                                                        <Trash2 className="w-5 h-5" />
                                                        <span className="hidden md:inline">Quitar</span>
                                                    </>
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminDiscounts;
